import { ReactNode, useState, useMemo, useEffect, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box, Grid, styled, Tooltip } from '@mui/material'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, Typography } from '@carto/meridian-ds/components'
import { useAppDispatch, useAppSelector } from 'app/state'
import { accountLimitRequest, setAccountLimitStatus } from 'features/hubspot/state/hubspotSlice'
import { QuotaResult } from '../types'
import Bar from './QuotaStats/Bar'
import { additionWouldExceedQuota, isHardQuotaError } from '../utils'
import { QuotasRequestOptions } from 'features/hubspot/types'
import { RequestStatus } from 'features/common'
import { HubspotForm } from 'features/hubspot/ui/HubspotForm/HubspotForm'
import { ReduxAction } from 'app/state/action'
import { quotaEventRequest } from 'features/metrics/state/metricsSlice'
import { QuotaEventMetric } from 'features/metrics/types'
import settings from 'settings'
import intl from 'app/localization/intl'

const CurrentPlanBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: theme.spacing(3)
}))

const CurrentPlanText = styled(Typography)(() => ({
  position: 'absolute',
  right: '35%'
}))

const HubspotFormWrapper = styled(Box)(({ theme }) => ({
  height: theme.spacing(36)
}))

export type HardQuotaModalController = {
  additionWouldExceedQuota(callQuota?: QuotaResult, addend?: number): boolean | undefined
  open(callQuota?: QuotaResult)
  props: {
    open: boolean
    quota?: QuotaResult
    onCloseModal: () => void
  }
}

export function useHardQuotaModalController(
  externalQuota?: QuotaResult,
  action?: ReduxAction<unknown>
): HardQuotaModalController {
  const [open, setOpen] = useState<boolean>(false)
  const [quota, setQuota] = useState<QuotaResult | undefined>()
  const dismissResult = action?.dismissResult
  const actionError = action?.error

  useEffect(() => {
    if (isHardQuotaError(actionError)) {
      setOpen(true)
    }
  }, [actionError])

  return useMemo(() => {
    return {
      additionWouldExceedQuota(callQuota?: QuotaResult, addend?: number) {
        return additionWouldExceedQuota(callQuota || quota || externalQuota, addend)
      },
      open: (quota?: QuotaResult) => {
        if (quota) {
          setQuota(quota)
        }
        setOpen(true)
      },
      props: {
        open: Boolean(open && (quota || externalQuota)),
        quota: quota || externalQuota,
        onCloseModal: () => {
          dismissResult && dismissResult()
          setOpen(false)
        }
      }
    }
  }, [open, quota, externalQuota, dismissResult])
}

interface WouldExceedQuotaDialogProps {
  open: boolean
  onCloseModal: () => void

  title: ReactNode | string
  message: ReactNode | string
  alertMessage: ReactNode | string

  quota?: QuotaResult
  quotaTitle: ReactNode | string
  quotaRequestType?: QuotasRequestOptions
  quotaEventMetric?: QuotaEventMetric
}

function WouldExceedQuotaDialog({
  open,
  onCloseModal,
  title,
  message,
  alertMessage,
  quota,
  quotaTitle,
  quotaRequestType,
  quotaEventMetric
}: WouldExceedQuotaDialogProps) {
  const dispatch = useAppDispatch()
  const accountLimitStatus = useAppSelector((state) => state.hubspot.accountLimitStatus)
  const showHubspotForm = accountLimitStatus !== RequestStatus.Idle

  useEffect(() => {
    if (open) {
      dispatch(setAccountLimitStatus(RequestStatus.Idle))
    }
    if (open && quotaEventMetric) {
      dispatch(quotaEventRequest(quotaEventMetric))
    }
  }, [open, quotaEventMetric, dispatch])

  const handleClose = useCallback(() => {
    if (accountLimitStatus !== RequestStatus.Idle) {
      dispatch(setAccountLimitStatus(RequestStatus.Idle))
    }
    onCloseModal()
  }, [accountLimitStatus, dispatch, onCloseModal])

  if (!quota) {
    return null
  }

  return (
    <Dialog open={open} onClose={handleClose} height="auto">
      <DialogHeader title={title} onClose={handleClose} />

      {showHubspotForm ? (
        <HubspotFormWrapper>
          <HubspotForm status={accountLimitStatus} onDone={handleClose} />
        </HubspotFormWrapper>
      ) : (
        <>
          <DialogContent>
            <Typography variant="body2" color="textSecondary">
              {message}
            </Typography>

            <Grid>
              <Box mb={0.5} display="flex" flexDirection="row">
                <Box width="65%" flex="none" display="flex" flexDirection="row" justifyContent="space-between">
                  <Typography variant="body2" weight="strong">
                    {quotaTitle}
                  </Typography>
                  <Typography variant="body2">
                    <FormattedMessage
                      id="account.cards.stats.chart.indicator"
                      values={{
                        current: quota.current?.toLocaleString(),
                        max: quota.limit?.toLocaleString()
                      }}
                    />
                  </Typography>
                </Box>
                <Box display="flex" flex="1 1 auto" justifyContent={quota.period ? 'space-between' : 'flex-end'}>
                  {quota.period && <Typography variant="body2">/{quota.period}</Typography>}
                  <Typography variant="body2" color="textSecondary">
                    <FormattedMessage id="quotas.wouldExceedQuotaModal.upgrade" />
                  </Typography>
                </Box>
              </Box>
              <Bar current={65} limit={100} overLimit />
            </Grid>
            <CurrentPlanBox>
              <CurrentPlanText variant="caption">
                <FormattedMessage id="quotas.wouldExceedQuotaModal.currentPlan" />
              </CurrentPlanText>
            </CurrentPlanBox>
          </DialogContent>
          <DialogFooter
            alertContent={alertMessage}
            alertProps={{
              severity: 'warning'
            }}
          >
            <Tooltip
              title={!settings.enableHubspot ? intl.formatMessage({ id: 'hubspot.hubspotDisabledAlert' }) : undefined}
            >
              <Box component="span">
                <Button
                  disabled={!settings.enableHubspot}
                  variant="contained"
                  onClick={() => dispatch(accountLimitRequest(quotaRequestType ?? QuotasRequestOptions.ReachingQuotas))}
                >
                  <FormattedMessage id="common.button.wantToBeContacted" />
                </Button>
              </Box>
            </Tooltip>
          </DialogFooter>
        </>
      )}
    </Dialog>
  )
}

export default WouldExceedQuotaDialog
